import {
  ConditionAction,
  DrawOrderConstants,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IGameStateMachineProvider,
  RegularSpinsSoundModelComponent,
  RESOURCES_COMPONENT_TOKEN,
  ResourcesComponent,
  SoundInstance,
  T_RegularSpinsSoundModelComponent,
} from '@cgs/machines';
import { SpinResponse } from '@cgs/data-access';
import {
  HUD_CONTROLLER_TOKEN,
  TrifectaHudController,
  TrifectaStartPageComponent,
} from '@cgs/provider-trifecta';
import { SCENE_FACTORY_TOKEN, SceneFactory } from '@cgs/common';
import {
  Button,
  Compatibility,
  Container,
  Platform,
  SceneObject,
  T_Platform,
  Vector2,
} from '@cgs/wge';

export const SANTA_START_PAGE_COMPONENT_TOKEN = Symbol('SANTA_START_PAGE_COMPONENT_TOKEN');

const enum SceneNames {
  Scene = 'loading/scene',
  Logo = 'loading/sceneLogo',
  LogoNode = 'logo',
  Preloader = 'preloader',
  LogoOrientation = 'Modes',
  ContinueButton = 'btn_continue',
}

const enum StateNames {
  Splash = 'splash',
  ZoomIn = 'zoomIn',
  SplashOut = 'splashOut',
  Alpha = 'alpha',
  Landscape = 'horizontal',
  Portrait = 'vertical',
}

const enum SoundNames {
  ButtonClick = 'button_click',
}

// Background resize tuning (ported from the previous SantaPreloaderSDK).
const enum ResizeRatio {
  DesktopFirstBreakpoint = 0.47,
  DesktopSecondBreakpoint = 0.92,
  DesktopThirdBreakpoint = 1,
  MobileLandscapeBreakpoint = 0.5,
}

const DESKTOP_FIRST_SLOPE = 0.8;
const DESKTOP_SECOND_SLOPE = 0.9;
const DESKTOP_BASE_OFFSET = 1.0;
const DESKTOP_THIRD_FACTOR = 2;
const MOBILE_LANDSCAPE_OFFSET_FACTOR = 0.1;
const SPLASH_SCALE = 0.92;

export class SantaStartPageComponent extends TrifectaStartPageComponent {
  private readonly container: Container;
  private readonly sceneFactory: SceneFactory;
  private readonly root: SceneObject;
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;

  private startPageClosed: boolean = false;

  private startPage: SceneObject;
  private scene: SceneObject;

  private originalBackgroundScale: Vector2;

  constructor(container: Container) {
    super();

    this.container = container;
    this.sceneFactory = container.resolve<SceneFactory>(SCENE_FACTORY_TOKEN);
    this.root = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).root;
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.gameStateMachine.init.entered.listen(() => this.show());
    this.gameStateMachine.init.appendLazyAnimation(
      () => new ConditionAction(() => this.startPageClosed)
    );
  }

  // loading.zip is preloaded by the game's CustomLoader before init, so the
  // scenes are already cached here and can be built synchronously — no async
  // wait, so the start page appears in the same frame as init (no slot flash).
  private show(): void {
    this.startPage = new SceneObject();
    this.startPage.z = DrawOrderConstants.Top;

    this.scene = this.sceneFactory.build(SceneNames.Scene)!;
    this.scene.initialize();
    this.startPage.addChild(this.scene);
    this.root.addChild(this.startPage);

    this.playIntro();
  }

  private playIntro(): void {
    const logoScene = this.sceneFactory.build(SceneNames.Logo) as SceneObject;

    const logoOrientation = this.scene.findById<SceneObject>(SceneNames.LogoOrientation);
    const continueButton = this.scene.findAllById<Button>(SceneNames.ContinueButton);

    const platform = this.container.resolve<Platform>(T_Platform);

    const reelSoundModel = this.container.resolve<RegularSpinsSoundModelComponent>(
      T_RegularSpinsSoundModelComponent
    ).regularSpinSoundModel;
    const buttonClick = reelSoundModel.getSoundByName(SoundNames.ButtonClick);

    this.originalBackgroundScale = logoScene.scale.clone();

    platform.view.sizeChanged.listen(() => {
      this.handleOrientationChange(logoOrientation!);
      this.resize(logoScene);
    });

    this.handleOrientationChange(logoOrientation!);

    if (!continueButton) {
      throw new Error('Continue button not found');
    }

    const hudController =
      this.container.resolve<TrifectaHudController>(HUD_CONTROLLER_TOKEN);

    const preloader = this.scene.findAllById<SceneObject>(SceneNames.Preloader);
    const logo = logoScene.findAllById<SceneObject>(SceneNames.LogoNode);
    const skipVideoButton = logoScene.findById<Button>(SceneNames.ContinueButton);

    if (!logo) {
      throw new Error('Logo not found');
    }

    if (!preloader) {
      throw new Error('Preloader not found');
    }

    if (!skipVideoButton) {
      throw new Error('Skip video button not found');
    }

    logoScene.initialize();

    this.scene.addChild(logoScene);

    preloader.forEach((p) => {
      p.stateMachine!.switchToState(StateNames.Splash);
    });

    this.resize(logoScene);

    if (hudController.isSkipIntro) {
      this.handleSkipIntro(preloader, continueButton, buttonClick);
      return;
    }

    preloader.forEach((p) => {
      p.stateMachine!.findById(StateNames.SplashOut)!.enterAction.done.listen(() => {
        this.handleOrientationChange(logoOrientation!);
      });
    });

    logo.forEach((l) => {
      l.stateMachine!.findById(StateNames.ZoomIn)!.enterAction.done.listen(() => {
        this.close();
      });
    });

    continueButton.forEach((button) => {
      const subscribe = button.clicked.listen(() => {
        buttonClick.stop();
        buttonClick.play();

        preloader.forEach((p) => {
          p.stateMachine!.switchToState(StateNames.SplashOut);
        });

        logo.forEach((l) => {
          l.stateMachine!.switchToState(StateNames.ZoomIn);
        });

        this.handleSkipIntro(preloader, continueButton, buttonClick);
        this.handleSkipIntro(preloader, [skipVideoButton], buttonClick);

        subscribe.cancel();
      });
    });
  }

  private handleSkipIntro(
    preloader: SceneObject[],
    continueButton: Button[],
    buttonClick: SoundInstance
  ): void {
    continueButton.forEach((button) => {
      const subscribe = button.clicked.listen(() => {
        buttonClick.stop();
        buttonClick.play();

        preloader.forEach((p) => {
          p.stateMachine!.switchToState(StateNames.Alpha);
        });

        this.close();

        subscribe.cancel();
      });
    });
  }

  private close(): void {
    if (this.startPageClosed) {
      return;
    }

    this.startPageClosed = true;
    this.onClose.emit();

    // close() can be called from a state's animation-done callback, which fires
    // inside StateMachine.update() (updateImpl reads this._current.rules right
    // after state.update()). Deinitializing the scene synchronously would null
    // that state mid-update and crash. Defer teardown past the current tick.
    void Promise.resolve().then(() => {
      this.root.removeChild(this.startPage);
      this.startPage.deinitialize();
    });
  }

  private handleOrientationChange(logoOrientation: SceneObject): void {
    logoOrientation.stateMachine!.switchToState(
      Compatibility.isPortrait ? StateNames.Portrait : StateNames.Landscape
    );
  }

  private resize(logoScene: SceneObject): void {
    const sceneScale = this.resizeSplashScreen();
    this.resizeBackground(logoScene, sceneScale);
  }

  private resizeBackground(logoScene: SceneObject, sceneScale: number): void {
    const logoSize = logoScene.findById('sizeFrame')!.size;

    const ratio = window.innerHeight / window.innerWidth;

    const imageWidth = logoSize.x * this.originalBackgroundScale.x;
    const imageHeight = logoSize.y * this.originalBackgroundScale.y;
    const imageRatio = imageWidth / imageHeight;

    if (!Compatibility.IsMobileBrowser) {
      let scale = 1 / (imageRatio * ratio);

      if (ratio > ResizeRatio.DesktopFirstBreakpoint) {
        scale = DESKTOP_FIRST_SLOPE * ratio + DESKTOP_BASE_OFFSET;
      }
      if (ratio > ResizeRatio.DesktopSecondBreakpoint) {
        scale = DESKTOP_SECOND_SLOPE * ratio + DESKTOP_BASE_OFFSET;
      }
      if (ratio > ResizeRatio.DesktopThirdBreakpoint) {
        scale = ratio * DESKTOP_THIRD_FACTOR;
      }

      logoScene.scale = new Vector2(scale, scale).divide(new Vector2(sceneScale, sceneScale));
      return;
    }

    if (Compatibility.isPortrait) {
      const scale = sceneScale * imageRatio;

      logoScene.scale = new Vector2(scale, scale);
    }

    if (!Compatibility.isPortrait) {
      let scale = sceneScale + imageRatio * MOBILE_LANDSCAPE_OFFSET_FACTOR;

      if (ratio > ResizeRatio.MobileLandscapeBreakpoint) {
        scale = imageRatio * ratio;
      }

      logoScene.scale = new Vector2(scale, scale);
    }
  }

  private resizeSplashScreen(): number {
    let scale = 1;
    if (!Compatibility.IsMobileBrowser) {
      scale = SPLASH_SCALE;
    }

    if (!Compatibility.isPortrait) {
      scale = SPLASH_SCALE;
    }

    this.scene.scale = new Vector2(scale, scale);

    return 1;
  }
}
